import type { Kennzahl, GetValuesFn } from './types'

// 16 visuell gut unterscheidbare Farben — zyklisch verwendet, falls die
// Daten mehr als 16 verschiedene Entitäten enthalten.
const BUNDESLAND_COLORS = [
    '#004699',
    '#009EE3',
    '#E5007D',
    '#E53009',
    '#FFA500',
    '#228B22',
    '#8B4513',
    '#4B0082',
    '#00CED1',
    '#FFD700',
    '#FF1493',
    '#5F9EA0',
    '#D2691E',
    '#A52A2A',
    '#8A2BE2',
    '#FF6347'
]

// ISO-3166-2:DE-Kürzel für kompakte Legenden-Labels.
// Unbekannte Namen fallen auf den Originalnamen zurück.
const BUNDESLAND_KUERZEL: Record<string, string> = {
    'Baden-Württemberg': 'BW',
    Bayern: 'BY',
    Berlin: 'BE',
    Brandenburg: 'BB',
    Bremen: 'HB',
    Hamburg: 'HH',
    Hessen: 'HE',
    'Mecklenburg-Vorpommern': 'MV',
    Niedersachsen: 'NI',
    'Nordrhein-Westfalen': 'NW',
    'Rheinland-Pfalz': 'RP',
    Saarland: 'SL',
    Sachsen: 'SN',
    'Sachsen-Anhalt': 'ST',
    'Schleswig-Holstein': 'SH',
    Thüringen: 'TH'
}

/**
 * Baut pro Entität (Bundesland / Herkunftsland) ein Dataset auf.
 * Der `wert` jeder Zeile ist eine kommagetrennte Liste von Entitätsnamen;
 * die Häufigkeit eines Namens pro Semester ergibt den Y-Wert.
 *
 * Verwendung: 2.2. (nach Bundesland), 3.2. (nach Herkunftsland)
 */
export function buildBundeslandChartData(kennzahl: Kennzahl, getValues: GetValuesFn, baseYears: string[]): any {
    const rows = getValues(kennzahl.sourceKennzahlId ?? kennzahl.kennzahlId)

    // Pro Semester: Map<Entitätsname, Häufigkeit>
    const countsBySemester: Record<string, Record<string, number>> = {}
    const entitySet = new Set<string>()

    rows.forEach((row) => {
        const counts: Record<string, number> = {}
        const raw = typeof row.wert === 'string' ? row.wert : ''
        raw.split(',').forEach((token) => {
            const name = token.trim()
            if (!name) return
            counts[name] = (counts[name] ?? 0) + 1
            entitySet.add(name)
        })
        countsBySemester[row.semester] = counts
    })

    if (entitySet.size === 0) return false

    // Alphabetisch sortiert (deutsche Locale) für stabile Farb- und Legenden-Reihenfolge
    const entities = Array.from(entitySet).sort((a, b) => a.localeCompare(b, 'de'))

    let maxValue = 0
    const datasets = entities.map((entity, idx) => {
        // null für Semester ohne diesen Eintrag → Linienunterbrechung (spanGaps: false)
        const dataPoints = baseYears.map((semester) => {
            const counts = countsBySemester[semester]
            if (!counts || !(entity in counts)) {
                return { x: semester, y: null as number | null }
            }
            const y = counts[entity]
            if (y > maxValue) maxValue = y
            return { x: semester, y }
        })

        const color = BUNDESLAND_COLORS[idx % BUNDESLAND_COLORS.length]

        return {
            label: entity,
            kuerzel: BUNDESLAND_KUERZEL[entity] ?? entity,
            data: dataPoints,
            borderColor: color,
            backgroundColor: color,
            tension: 0,
            borderWidth: 2,
            pointRadius: 2,
            fill: false,
            spanGaps: false
        }
    })

    // maxValue auf das erste Dataset — LineChart liest die Skalierung von datasets[0].maxValue.
    const scaledMax = maxValue ? maxValue * 1.15 : 10
    ;(datasets[0] as Record<string, unknown>).maxValue = scaledMax

    return { labels: baseYears, datasets }
}
